// Percorso file: src/utils/uniforms.js
//
// SEDE UNICA delle divise: chi le riceve, quali tipi esistono, come si
// chiamano i tre stati di consegna.
//
// ⚠️ MODULO PURO, e deve restarlo: nessun React, nessun hook, nessuna
// chiamata di rete. Si importa il gate unico dei ruoli (`auth/access` per
// via di `utils/roleUtils`, che lo ri-esporta), la sede unica della
// lettura di squadra (`utils/squadre`) e — dal cantiere UNIFORMS-ANNO — il
// metro dell'anno (`utils/stagione`) e l'anno storico (`utils/quote`, che
// importa solo `stagione`), e basta. Nessuno di loro importa questo file:
// non si apre alcun ciclo.
import { canonicalRole } from "./roleUtils";
import { primaSquadraDellaTessera } from "./squadre";
import { annoDiLista } from "./stagione";
import { STAGIONE_STORICA } from "./quote";

// SEDE UNICA della regola «chi riceve divise per RUOLO».
//
// Le divise si attribuiscono per due vie, e sono alternative:
//   - i GIOCATORI le ricevono per SQUADRA (`uniformConfig.teams[user.team]`,
//     chiavi = nomi squadra, non ruoli);
//   - tutti gli altri le ricevono per RUOLO (`uniformConfig.roles[RUOLO]`),
//     ma solo se il loro ruolo è in questo elenco.
//
// ⚠️ DECISIONE DEL CAPOMASTRO (9/8/2026, P4, D5): ricevono divise per ruolo
// i SOLI COACH. I genitori restano esclusi anche dall'elenco della pagina
// Uniform Distribution (UniformDistribution.jsx), e ADMIN/SUPERADMIN non
// maturano alcuna divisa.
//
// Cambiamento DICHIARATO rispetto alla lettera del codice precedente: la
// vecchia `ROLE_MAP` di UniformDistribution.jsx elencava fra i destinatari
// anche `Admin`, `SuperAdmin` e `Parent` (quest'ultimo senza effetto lì,
// perché i genitori erano comunque esclusi dall'elenco della pagina), e la
// scheda profilo non filtrava affatto i destinatari: leggeva
// `config.roles[<ruolo del documento>]`, qualunque fosse. Nessun effetto
// osservabile cambia oggi: al 9/8/2026 `uniformConfig.roles` in beta è
// VUOTO (verifica in console del capomastro), quindi nessuna divisa è mai
// stata configurata per nessuno di loro.
//
// ⚠️ Le chiavi sono CANONICHE e MAIUSCOLE: si confrontano sempre con
// `canonicalRole` (auth/access.js), mai con la grafia grezza del documento.
// Così un documento storico `role: "Manager"` — che il vocabolario traduce
// COACH — riceve le divise dell'allenatore, ed è l'esito voluto (decisione 2
// dell'apertura U1-F, confermata in P4/D2).
//
// ⚠️ Le etichette morte («Fan/Supporter», «eSport Member», «Deleted
// Record»), che la vecchia `ROLE_MAP` doveva nominare una per una per
// mandarle a `null`, qui non compaiono affatto: non essendo in elenco, non
// ricevono nulla. Non sono ruoli e non tornano (rapporto 11 §2.3 c.3).
//
// ⚠️ PLAYER non va aggiunto qui, mai: i giocatori passano dalla squadra, e
// UniformConfig toglie di proposito la riga PLAYER dalla tabella «By Role».
// Mettendolo in elenco, i due lettori onorerebbero una chiave che la
// configurazione non permette di spuntare.
//
// ⚠️ CONDIZIONE DI DEPLOY IN PRODUZIONE, da verificare prima del travaso
// post-27/9 (in beta il caso non si dà: `uniformConfig.roles` è vuoto).
// La lettura è ora a chiave CANONICA MAIUSCOLA: `config.roles["COACH"]`.
// Prima di P4 le due pagine leggevano la chiave con la GRAFIA DEL DOCUMENTO
// UTENTE — `config.roles["Coach"]` per un `role: "Coach"` — che è anche la
// grafia con cui l'unica interfaccia mai esistita può averle scritte.
//
// ⚠️ LA REGOLA GENERALE, che conta più dell'elenco: dopo P4 l'unica chiave
// di `roles` che qualcuno legge è **"COACH"**. Qualunque altra chiave
// valorizzata va considerata PERSA. L'elenco qui sotto raccoglie i casi che
// ci si aspetta di incontrare, non il confine del problema.
//
// In `app-qpsc` il documento `uniformConfig/main` va quindi guardato in
// console PRIMA del travaso; ogni chiave diversa da "COACH" si sana
// rispuntando le divise, una volta sola, sulla riga canonica.
//   1. chiave `"Coach"` capitalizzata → non più letta (ora si cerca "COACH");
//   2. chiavi `"Manager"` / `"TD"` / `"ManagerTD"` → nella SCHEDA PROFILO
//      erano lette tali e quali per i documenti con quel ruolo, ora quelle
//      persone leggono "COACH" (il vocabolario le traduce) e la vecchia
//      chiave resta muta. Nella pagina di DISTRIBUZIONE il caso non si dava
//      già prima per "TD" e "ManagerTD" (la vecchia ROLE_MAP non aveva
//      quelle voci), e si dava per "Manager";
//   3. chiavi `"Admin"` / `"SuperAdmin"` eventualmente valorizzate → quelle
//      divise spariscono, perché ADMIN e SUPERADMIN escono dai destinatari
//      (D5, qui sopra);
//   4. chiave `"Parent"` → stesso destino, e per la SOLA scheda profilo: la
//      pagina di distribuzione escludeva già i genitori dall'elenco, ma la
//      scheda leggeva `config.roles["Parent"]` senza filtro sui destinatari
//      e quelle divise le mostrava;
//   5. ⚠️ chiave `"Child"` — il caso da cercare con più attenzione, perché
//      è l'unico che NON SI VEDE: la riga «Child» non compare nemmeno nella
//      tabella «By Role» (il filtro la manda via col PLAYER), quindi non ci
//      si accorge che esiste. Prima la scheda profilo leggeva
//      `config.roles["Child"]`; ora quei minori passano dal ramo SQUADRA.
//      Non «spariscono» soltanto: cambiano di ramo;
//   6. chiave `"PLAYER"` in grafia nuova, e le etichette morte
//      (`"Fan/Supporter"`, `"eSport Member"`, `"Deleted Record"`): mute
//      nella distribuzione già prima, ma LETTE nella scheda profilo, che
//      non filtrava nulla.
// In beta nessuno di questi casi si dà — `roles` è vuoto — ma in produzione
// non li ha verificati nessuno.
// Rilievi dei Revisori di P4 (S4, m2, R1, S2), a verbale nella consegna.
//
// Sede unica e non copia locale per la ragione di sempre: le definizioni
// duplicate in sedi diverse sono l'origine del difetto U1-A (doppia idoneità
// Play Football, tre esemplari). Se il club cambierà idea, si tocca questo
// file e basta.
export const RUOLI_CON_DIVISE = ["COACH"];

/* =========================================================================
   I TIPI DI DIVISA — sede unica del RIPIEGO (UNIFORMS/D7, poi UNIFORMS-UI/
   Fetta B)
   ========================================================================= */

/**
 * Gli otto capi STORICI del club, nell'ordine in cui si sono sempre mostrati.
 *
 * ⚠️ DALLA FETTA B (19/9/2026) QUESTO ELENCO È IL RIPIEGO, NON PIÙ LA LISTA.
 * La lista dei capi vive nel catalogo dell'anno — `uniformConfig/{anno}.items`,
 * un array di `{ id, label, hasSize, active, order }` che la modale di
 * configurazione scrive e `GET /uniform-config` rende (`items`, o `null` se
 * il documento non la porta). Quando `items` è `null` — un'annata su cui
 * nessun admin ha ancora salvato la lista — pagina e modale costruiscono da
 * questi otto nomi la lista predefinita (`capiPredefiniti`, qui sotto), con
 * `id = label = nome`: è ESATTAMENTE il comportamento di prima della fetta,
 * e nulla si rompe prima del primo salvataggio.
 *
 * ⚠️ NIENTE QUI È NUOVO: è l'UNIONE ESATTA delle tre copie locali che
 * vivevano nelle pagine, e nient'altro. Le tre erano:
 *   - `UniformDistribution.jsx` — otto voci, completa;
 *   - `UniformConfig.jsx`       — otto voci, identica alla prima;
 *   - `MemberProfileModal.jsx`  — SETTE voci: mancava `Polo-Shirt`.
 * La terza è la copia MONCA del difetto U-Q3: la scheda del socio non
 * mostrava mai la riga «Polo-Shirt», anche quando il catalogo la spuntava
 * per la sua squadra, e la modale di configurazione la offriva.
 *
 * ⚠️ NON SI INVENTANO TIPI, E NON SE NE TOLGONO. I nomi sono la CHIAVE con
 * cui si leggono `uniformConfig.roles[...]` e `uniformConfig.teams[...]` e
 * con cui si scrivono le consegne — `user.uniformsSeasons[anno][...]`, e nel
 * piatto `user.uniforms[...]`, che è il 2026 storico (vedi `consegneDellAnnata`
 * qui sotto): per gli otto storici l'id del capo È il nome (così nessuna
 * consegna registrata va migrata, UNIF3-1 §2.4).
 * Toglierne uno da qui fa sparire consegne già registrate nei documenti di
 * chi non ha ancora una lista salvata. Un capo che il club non usa più si
 * SPEGNE dalla modale (`active: false`): SPEGNERE ≠ TOGLIERE — il capo
 * sparisce dalla vista, resta nella lista e le sue consegne restano nel dato.
 * Un capo nuovo si AGGIUNGE dalla modale, non qui: il suo id lo assegna il
 * server, opaco, mai derivato dal nome.
 */
export const TIPI_DIVISA = [
  "Training Kit",
  "RainJacket",
  "Small Bag",
  "Game Kit",
  "Tracksuit",
  "Polo-Shirt",
  "Presentation Kit",
  "Big Bag"
];

/**
 * La lista PREDEFINITA dei capi — il ripiego quando il catalogo dell'anno non
 * porta `items` (UNIFORMS-UI/Fetta B).
 *
 * Otto voci nella forma del catalogo, `{ id, label, hasSize, active, order }`,
 * con `id = label = nome storico` e `order` = posizione nell'elenco.
 *
 * ⚠️ `hasSize: true` PER TUTTI GLI OTTO, borse comprese — SCELTA PRUDENTE,
 * NON DI PRODOTTO: con `true` nulla di ciò che si vede oggi sparisce (la
 * tendina taglia resta dov'era su ogni capo). Se il committente decide che
 * «Small Bag» e «Big Bag» non hanno taglia, la modifica è qui e in un punto
 * solo; finché la lista non è salvata dalla modale, questo ripiego è ciò che
 * la pagina mostra. Referto UNIF3-4, scelta (a) lasciata al committente.
 *
 * Torna un array NUOVO a ogni chiamata: chi lo riceve può mutarlo (la modale
 * lo usa come stato di partenza) senza toccare la sede unica.
 */
export const capiPredefiniti = () =>
  TIPI_DIVISA.map((nome, indice) => ({
    id: nome,
    label: nome,
    hasSize: true,
    active: true,
    order: indice
  }));

/**
 * La lista dei capi da usare dato il campo `items` del catalogo: quella
 * salvata se c'è (un array), altrimenti il ripiego. È l'UNICO punto in cui la
 * regola «items null → gli otto storici» è scritta: pagina e modale la
 * chiamano, non la ricopiano.
 */
export const capiDelCatalogo = (items) =>
  Array.isArray(items) ? items : capiPredefiniti();

/**
 * I capi in ORDINE di `order` — tutti, spenti compresi (la modale li elenca
 * tutti). Copia ordinata: l'array ricevuto non si muta. Le voci senza `id`
 * stringa si scartano: senza chiave non c'è nulla da leggere né da scrivere.
 */
export const capiOrdinati = (capi) =>
  (Array.isArray(capi) ? capi : [])
    .filter((capo) => capo && typeof capo.id === "string" && capo.id !== "")
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

/**
 * I soli capi ATTIVI, in ordine: sono le colonne della pagina e delle tabelle
 * di spunta della modale. Un capo è spento SOLO se `active === false`: una
 * voce senza il campo (documento scritto a mano) si legge accesa, così
 * nulla sparisce per un campo mancante.
 */
export const capiAttivi = (capi) =>
  capiOrdinati(capi).filter((capo) => capo.active !== false);

/**
 * «Questo capo ha la taglia?» — `hasSize` con lo stesso metro prudente:
 * SOLO un `false` esplicito toglie la tendina taglia.
 */
export const capoHaTaglia = (capo) => !capo || capo.hasSize !== false;

/* =========================================================================
   I TRE STATI DI CONSEGNA — sede unica (UNIFORMS/D7)
   ========================================================================= */

/**
 * Le etichette a schermo dei tre stati di una cella di consegna
 * (`{ status, size }`, letta con `consegneDellAnnata(user, anno)[tipo]`).
 *
 * ⚠️ I VALORI NON SI TOCCANO: `not_delivered`, `delivered` e `no` sono le
 * stringhe che stanno nei documenti Firestore. Qui si dà loro un NOME, non
 * un valore nuovo.
 *
 * ⚠️ LE DUE COPIE DIVERGEVANO, ed è il difetto che questa sede chiude: la
 * pagina di distribuzione rendeva `"no"` con «NO»
 * (`UniformDistribution.jsx`), la scheda del socio con «N/A»
 * (`MemberProfileModal.jsx`) — stesso dato, due parole diverse, a
 * seconda di dove lo si guardava.
 *
 * ETICHETTA SCELTA PER `"no"`: «N/A». Le due candidate erano già in uso; si
 * tiene quella della SCHEDA perché è la pagina che leggono anche i soci e i
 * genitori, e perché «NO» in una tendina accanto a «Delivered» e «Not
 * Delivered» si legge come un rifiuto, mentre il significato è «questa
 * divisa qui non si applica». (Testi a schermo in inglese, CLAUDE.md §3.7.)
 */
export const ETICHETTE_STATO = {
  not_delivered: "Not Delivered",
  delivered: "Delivered",
  no: "N/A"
};

/**
 * L'etichetta di uno stato, con ripiego sul valore grezzo.
 *
 * Uno stato fuori dai tre — un documento storico con una grafia diversa —
 * si mostra com'è invece di sparire: è la stessa dottrina di `roleLabel`
 * (access.js), che il canonico che non sa tradurre lo restituisce tale e
 * quale.
 */
export const etichettaStato = (stato) =>
  ETICHETTE_STATO[stato] || String(stato ?? "");

/* =========================================================================
   LA SPETTANZA — sede unica del criterio (UNIFORMS/D1)
   ========================================================================= */

/**
 * QUALI DIVISE SPETTANO A UNA PERSONA, dalle sue TESSERE dell'anno.
 *
 * ⚠️ IL CRITERIO ERA DUPLICATO, e le due copie erano già divergenti:
 *   - `UniformDistribution.jsx` (`divisaSpettante`) leggeva
 *     `canonicalRole(user.role)` e `config.teams[user.team]`, cioè DUE
 *     CAMPI DI PERSONA;
 *   - `MemberProfileModal.jsx` usava lo stesso criterio con un
 *     corpo diverso e già asimmetrico: la SQUADRA veniva dalla tessera
 *     operativa (`squadraDelSoggetto`), il RUOLO ancora dal documento
 *     (difetto U-Q9).
 * Qui il criterio è uno solo e legge SOLO LE TESSERE: `user.role` e
 * `user.team` non entrano più in questa decisione. Il campo di persona
 * `team` è una squadra sola per PERSONA — la stessa riga sia che giochi
 * nell'U12, sia che alleni l'U10 — ed è il baco che il modello a tessere
 * chiude (vedi `utils/squadre.js`).
 *
 * LE REGOLE (decisione D1 del committente):
 *   - tessera PLAYER valida dell'anno → i tipi di `config.teams[<la sua
 *     squadra>]`. UN GIOCATORE HA UNA SOLA SQUADRA (invariante del club,
 *     2/9/2026): si legge quella della sua tessera, senza alcuna logica
 *     multi-squadra;
 *   - tessera di un ruolo con divise (`RUOLI_CON_DIVISE`, oggi il solo
 *     COACH) → i tipi di `config.roles[<quel ruolo>]`;
 *   - CHI HA ENTRAMBE → l'UNIONE. È «una squadra + un ruolo» — il genitore
 *     che allena, il ragazzo grande che è anche assistente — non «due
 *     squadre»;
 *   - PARENT e tessere amministrative → nulla: non sono in
 *     `RUOLI_CON_DIVISE` e non hanno il ramo squadra.
 *
 * ⚠️ LA SQUADRA SI LEGGE CON `primaSquadraDellaTessera` (`utils/squadre.js`),
 * la primitiva già scritta per l'invariante «un player, una squadra»: non
 * si fa l'unione delle tessere e non si legge `allTeams` — su una tessera
 * non-COACH nessun lettore onora «tutte le squadre».
 *
 * ⚠️ LE CHIAVI DI `config.roles` SONO CANONICHE E MAIUSCOLE; quelle di
 * `config.teams` sono i NOMI DELLE SQUADRE e NON si normalizzano (verifica
 * in console del capomastro, 9/8/2026).
 *
 * @param {Array}  tessere le tessere GIÀ FILTRATE sull'anno guardato — le
 *                 dà `tessereDelSoggetto(user.contexts, anno)`
 *                 (`utils/squadre.js`). Questa funzione non filtra per
 *                 anno: non conosce l'anno, e non deve conoscerlo.
 * @param {Object} config il catalogo `{ roles, teams }` dell'anno.
 * @returns {Set<string>} i tipi che spettano. Vuoto se il catalogo manca.
 */
export const tipiDivisaSpettanti = (tessere, config) => {
  const spettanti = new Set();

  // Guardia difensiva, non rimedio a un difetto: una sede unica non deve
  // dipendere dalla cortesia di chi la chiama. Senza, un catalogo non
  // ancora arrivato — o assente, che dopo UNIFORMS/Fetta A è un caso
  // ORDINARIO e non un guasto — farebbe cadere il disegno invece di non
  // attribuire divise.
  if (!config) return spettanti;

  const valide = Array.isArray(tessere) ? tessere : [];

  // RAMO SQUADRA. Una sola tessera PLAYER, una sola squadra: si prende la
  // prima che si incontra. Se ce ne fossero due, l'invariante del club è
  // rotta a monte e il posto per accorgersene non è questa funzione.
  const tesseraGiocatore = valide.find(
    (ctx) => canonicalRole(ctx?.role) === "PLAYER"
  );

  if (tesseraGiocatore) {
    const squadra = primaSquadraDellaTessera(tesseraGiocatore);
    const divise = squadra ? config.teams?.[squadra] : null;
    if (Array.isArray(divise)) divise.forEach((tipo) => spettanti.add(tipo));
  }

  // RAMO RUOLO. Si scorrono TUTTE le tessere: chi ha due tessere COACH
  // nello stesso anno riceve comunque le divise del ruolo una volta sola,
  // perché l'insieme non ammette doppioni.
  valide.forEach((ctx) => {
    const ruolo = canonicalRole(ctx?.role);
    if (!RUOLI_CON_DIVISE.includes(ruolo)) return;

    const divise = config.roles?.[ruolo];
    if (Array.isArray(divise)) divise.forEach((tipo) => spettanti.add(tipo));
  });

  return spettanti;
};

/**
 * «Questo tipo di divisa spetta a questa persona?» — la domanda singola.
 *
 * Comodità per chi ha un tipo solo da verificare. Chi ne deve verificare
 * otto (una riga di tabella) chiami `tipiDivisaSpettanti` UNA volta e
 * interroghi l'insieme: qui dentro l'insieme si ricostruisce a ogni
 * chiamata, ed è esattamente lo spreco che la pagina di distribuzione
 * faceva sedici volte per riga.
 */
export const divisaSpettante = (tessere, config, tipo) =>
  tipiDivisaSpettanti(tessere, config).has(tipo);

/* =========================================================================
   LE CONSEGNE DELL'ANNATA — sede unica della regola con cui il browser
   sceglie DOVE stanno le consegne di (persona, anno) (UNIFORMS-ANNO / Fetta 3)
   ========================================================================= */

// ⚠️ È IL CALCO DI `sedeDelleConsegne` (backend/server.js, Fetta 1) e la
// gemella di `quoteDellAnnata` (utils/quote.js): non una seconda regola. Il
// backend la applica in lettura e in scrittura insieme — è quello che
// impedisce alle due cose di divergere — e qui si ripete perché il browser
// non condivide codice col server. Se la regola del backend cambia, cambia
// anche questa: sono due copie DICHIARATE di un'unica dottrina.
//
// DOVE VIVONO LE CONSEGNE, sul documento persona:
//   · `uniforms` — il campo PIATTO, senz'anno, che È la stagione 2026
//     storica. Dopo il battesimo (Fetta 2) resta dov'è come FOTOGRAFIA:
//     non è più aggiornato, e chi lo legge direttamente vede un dato fermo
//     (è la trappola di `user.fees` per le quote);
//   · `uniformsSeasons` — la mappa PER ANNATA, chiave = l'anno in cifre,
//     valore = la mappa delle celle `{ [idCapo]: { status, size } }`.
//
// LA REGOLA, per l'anno guardato Y:
//   · `uniformsSeasons[Y]` se quella chiave c'è;
//   · altrimenti, e SOLO per il 2026, il campo piatto `uniforms`;
//   · altrimenti — anno diverso, chiave assente — nessuna consegna.
//
// ⚠️ NESSUN RIPIEGO SUL CAMPO SENZ'ANNO FUORI DAL 2026: un 2027 senza
// consegne è un 2027 senza consegne, non un 2027 che eredita il 2026.
//
// ⚠️ QUESTA È L'UNICA COPIA DELLA REGOLA LATO FRONTEND. La pagina Uniform
// Distribution (cella e salvataggio), la scheda del socio e il report
// (`statistiche-divise.js`) la CHIAMANO: nessuno legge `user.uniforms` o
// `user.uniformsSeasons` per conto suo. Non scriverne una terza copia
// (CLAUDE.md §2, lezione delle quote).

// Una mappa, o `{}` se il valore non lo è. Esplicita perché `typeof null` e
// `typeof []` sono entrambi "object" (calco di `mappaOVuota`, server.js).
const mappaOVuota = (valore) =>
  valore && typeof valore === "object" && !Array.isArray(valore) ? valore : {};

/**
 * DOVE stanno le consegne di (persona, anno): la SEDE, come la dà
 * `sedeDelleConsegne` del backend — `annata: true` con la chiave della mappa
 * per anno, o `annata: false` per il piatto (solo 2026 non battezzato).
 *
 * ⚠️ NON È SOLO LETTURA, ed è il motivo per cui la sede è esportata e non
 * solo la mappa: la pagina, dopo un salvataggio, aggiorna lo stato locale
 * IMMUTABILMENTE nel posto in cui il backend ha scritto — la fetta dell'anno
 * o il piatto — e per saperlo deve chiedere qui, non indovinare. Se creasse
 * localmente una fetta 2026 con la sola cella toccata mentre il documento è
 * ancora sul piatto, la lettura successiva prenderebbe quella fetta e le altre
 * consegne del 2026 SPARIREBBERO dalla vista: è il «travaso a metà» che il
 * backend evita (UNIF4-2 §2.2), e il browser deve evitarlo con la stessa
 * regola. Le quote non hanno questo bisogno (la finestra Payments RILEGGE il
 * documento dopo ogni scrittura, `ricaricaEPropaga`): qui il calco di
 * `quoteDellAnnata` si estende di un passo, dichiarato.
 *
 * ⚠️ ANNATA IGNOTA (`anno` non intero) → `null`: senza un anno la domanda
 * «dove stanno le consegne NELL'ANNO?» non ha oggetto (stessa guardia di
 * `quoteDellAnnata`, che rende il record vuoto).
 *
 * ⚠️ LA CHIAVE SI CERCA CON `annoDiLista`, non con un `[String(anno)]` crudo:
 * la proiezione di lista (`consegneDiLista`, server.js) normalizza le chiavi,
 * ma `GET /users/:id` — la fonte della scheda del socio — risponde col
 * DOCUMENTO GREZZO. È la stessa scelta, e la stessa riga, di `quoteDellAnnata`,
 * con lo stesso avviso: è un SUPERSET della ricerca stretta del backend
 * (`hasOwnProperty(String(anno))`), innocuo finché le chiavi le scrivono solo
 * il backend e il battesimo — entrambi canonici, `String(<intero>)`.
 *
 * `grezzo` è il valore com'è sul documento (può non essere una mappa, o
 * mancare); `null` con `annata: true` vuol dire «chiave assente». La forma
 * garantita la dà `consegneDellAnnata`.
 *
 * @param {object} user il socio, dalla proiezione di lista o dal documento
 * @param {number|null} anno l'annata guardata
 * @returns {{annata: boolean, chiave: string, grezzo: *}|null}
 */
export const sedeDelleConsegne = (user, anno) => {
  if (!Number.isInteger(anno)) return null;

  const mappa = user?.uniformsSeasons;

  if (mappa && typeof mappa === "object" && !Array.isArray(mappa)) {
    const chiave = Object.keys(mappa).find((k) => annoDiLista(k) === anno);

    // ⚠️ SI GUARDA LA PRESENZA DELLA CHIAVE, non la verità del valore —
    // `hasOwnProperty`, come fa `sedeDelleConsegne` nel backend. Un'annata
    // esistente ma vuota È un'annata senza consegne, e non deve ricadere sul
    // campo piatto.
    if (chiave !== undefined) return { annata: true, chiave, grezzo: mappa[chiave] };
  }

  if (anno === STAGIONE_STORICA) {
    return { annata: false, chiave: String(anno), grezzo: user?.uniforms };
  }

  return { annata: true, chiave: String(anno), grezzo: null };
};

/**
 * Le consegne della persona per l'annata guardata: la mappa delle celle
 * `{ [idCapo]: { status, size } }` di quell'anno.
 *
 * Restituisce SEMPRE una mappa (nuova): se l'annata non si sa, se la sede non
 * porta una mappa o se quell'anno non ha voce, la mappa è vuota — cioè
 * «nessuna consegna per quest'anno», che è un'informazione, non un errore.
 * Il ripiego di cella («assente = Not Delivered, senza taglia») resta del
 * chiamante, com'era: qui non si inventano celle.
 *
 * ⚠️ LO SPREAD, come `recordDiQuote`: chi riceve la mappa può copiarla e
 * modificarla (la pagina lo fa) senza toccare il documento in stato.
 *
 * @param {object} user il socio, dalla proiezione di lista o dal documento
 * @param {number|null} anno l'annata guardata
 * @returns {Object<string, {status?: string, size?: string}>}
 */
export const consegneDellAnnata = (user, anno) => {
  const sede = sedeDelleConsegne(user, anno);
  return sede === null ? {} : { ...mappaOVuota(sede.grezzo) };
};
